import { useEffect, useState } from 'react';
import { salesApi, type SalesOrder } from '../lib/api';

function money(minor: number, currency: string) {
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

const NEXT_STATUS: Record<string, string[]> = {
  PAID: ['IN_PREPARATION'],
  IN_PREPARATION: ['SHIPPED'],
  SHIPPED: ['DELIVERED'],
};

export function SalesPage() {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [seats, setSeats] = useState<Array<{ user_id: string; granted_by: string | null }>>([]);
  const [audit, setAudit] = useState<Record<string, Array<{ action: string; actor_user_id: string; detail: string; created_at: string }>>>({});
  const [newSeat, setNewSeat] = useState('');
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);

  useEffect(() => { reload(); }, []);

  async function reload() {
    try {
      setOrders(await salesApi.orders());
      setDenied(false);
    } catch (e: any) {
      // Sans siège attribué, l'espace ventes est fermé : ce n'est pas une erreur.
      if (String(e.message || e).includes('403')) setDenied(true);
      else setError(String(e.message || e));
    }
    try { setSeats(await salesApi.seats()); } catch { /* réservé aux administrateurs */ }
  }

  async function act(fn: () => Promise<unknown>) {
    setError('');
    try { await fn(); await reload(); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  async function showAudit(orderId: string) {
    try { setAudit({ ...audit, [orderId]: await salesApi.audit(orderId) }); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  if (denied) return <section className="page">
    <header className="page-header"><div><span className="eyebrow">VENTES</span><h1>Espace ventes</h1></div></header>
    <div className="card"><p>Cet espace exige un siège attribué par un administrateur.</p></div>
  </section>;

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">VENTES</span>
        <h1>Commandes à traiter</h1>
        <p>Chaque action est tracée et attribuée à son auteur.</p>
      </div>
      <span className="status ok">{orders.length} commande(s)</span>
    </header>

    {error && <div className="alert error">{error}</div>}

    <div className="card">
      <h3>Sièges d'accès</h3>
      <div className="toolbar">
        <input value={newSeat} onChange={(e: any) => setNewSeat(e.target.value)} placeholder="identifiant utilisateur" />
        <button className="button secondary" disabled={!newSeat.trim()} onClick={() => act(() => salesApi.grantSeat(newSeat.trim()))}>Attribuer</button>
      </div>
      <div className="member-list">
        {seats.map(s => <button key={s.user_id} onClick={() => act(() => salesApi.revokeSeat(s.user_id))} title="Retirer le siège">
          <strong>{s.user_id.slice(0, 8)}</strong><span>retirer</span>
        </button>)}
        {!seats.length && <p className="technical-note">Aucun siège attribué.</p>}
      </div>
    </div>

    {orders.map(o => <div key={o.id} className="card">
      <h3>{o.item_title || o.item_kind}</h3>
      <p>
        {o.fulfilment === 'DIGITAL' ? 'Numérique' : 'Papier'} · {money(o.amount_minor, o.currency)} ·
        <strong> {o.status}</strong>
      </p>

      {o.fulfilment === 'PRINT' && <div className="shipping-block">
        <h4>Expédition</h4>
        <p>{o.buyer_first_name} {o.buyer_last_name}<br />
          {o.address_line}<br />
          {o.postal_code} {o.city} — {o.country}<br />
          Tél. {o.phone}</p>
      </div>}

      <div className="toolbar">
        {!o.assigned_admin_user_id && <button className="button secondary" onClick={() => act(() => salesApi.assign(o.id))}>Me l'attribuer</button>}
        {(NEXT_STATUS[o.status] || []).map(s =>
          <button key={s} className="button primary" onClick={() => act(() => salesApi.setStatus(o.id, s))}>Passer à {s}</button>)}
        <button className="button secondary" onClick={() => showAudit(o.id)}>Historique</button>
      </div>

      {audit[o.id] && <div className="audit-list">
        {audit[o.id].map((a, i) => <div key={i}>
          <strong>{a.action}</strong>
          <small>{a.actor_user_id?.slice(0, 8)} · {new Date(a.created_at).toLocaleString()}</small>
          {a.detail && <em>{a.detail}</em>}
        </div>)}
      </div>}
    </div>)}

    {!orders.length && <div className="card"><p>Aucune commande en attente.</p></div>}
  </section>;
}
